import { z } from "zod";

export const specialistOwnershipTypes = ["independent", "vendor_owned"] as const;
export const specialistOwnershipTypeSchema = z.enum(specialistOwnershipTypes);

export const specialistProfileStatuses = ["draft", "active", "archived"] as const;
export const specialistProfileStatusSchema = z.enum(specialistProfileStatuses);

export const specialistReferenceStatuses = ["active", "inactive"] as const;
export const specialistReferenceStatusSchema = z.enum(specialistReferenceStatuses);

export const specialistProfileSkillVerificationStates = ["unverified", "verified", "rejected"] as const;
export const specialistProfileSkillVerificationStateSchema = z.enum(
  specialistProfileSkillVerificationStates,
);

export const specialistProfileSkillSources = [
  "self_reported",
  "vendor_reported",
  "admin_added",
  "imported",
  "assessment",
] as const;
export const specialistProfileSkillSourceSchema = z.enum(specialistProfileSkillSources);

const nullableString = (maxLength: number) => z.string().trim().min(1).max(maxLength).nullable();
const nullableCountryCode = z.string().regex(/^[A-Z]{2}$/).nullable();
const nullableYearsOfExperience = z.number().int().min(0).max(80).nullable();
const slugSchema = z.string().regex(/^[a-z0-9]+(-[a-z0-9]+)*$/).max(100);
const referenceSortOrderSchema = z.number().int().min(0);
const isoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const nullableIsoDateSchema = isoDateSchema.nullable();
const skillLevelSchema = z.number().int().min(1).max(5);
const skillYearsSchema = z.number().int().min(0).max(80).nullable();
const skillLastUsedAtSchema = nullableIsoDateSchema;
const technologiesSchema = z.array(z.string().trim().min(1).max(80)).max(50);
const sortOrderSchema = z.number().int().min(0);

export const specialistReferenceDescriptionSchema = nullableString(2_000).optional();

export const specialistSpecializationSchema = z.object({
  id: z.string().uuid(),
  slug: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  status: specialistReferenceStatusSchema,
  sortOrder: z.number().int(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export const specialistGradeSchema = z.object({
  id: z.string().uuid(),
  slug: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  level: z.number().int(),
  status: specialistReferenceStatusSchema,
  sortOrder: z.number().int(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export const specialistSkillCategorySchema = z.object({
  id: z.string().uuid(),
  slug: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  status: specialistReferenceStatusSchema,
  sortOrder: z.number().int(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export const specialistSkillSchema = z.object({
  id: z.string().uuid(),
  categoryId: z.string().uuid().nullable(),
  slug: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  status: specialistReferenceStatusSchema,
  sortOrder: z.number().int(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export const specialistSkillSynonymSchema = z.object({
  id: z.string().uuid(),
  skillId: z.string().uuid(),
  term: z.string(),
  createdAt: z.string().datetime(),
});

export const specialistProfileSkillSchema = z.object({
  id: z.string().uuid(),
  specialistProfileId: z.string().uuid(),
  skillId: z.string().uuid(),
  level: z.number().int(),
  yearsOfExperience: z.number().int().nullable(),
  lastUsedAt: z.string().nullable(),
  isKeySkill: z.boolean(),
  verificationState: specialistProfileSkillVerificationStateSchema,
  source: specialistProfileSkillSourceSchema,
  createdByUserId: z.string().uuid(),
  verifiedAt: z.string().datetime().nullable(),
  verifiedByUserId: z.string().uuid().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export const specialistWorkExperienceSchema = z.object({
  id: z.string().uuid(),
  specialistProfileId: z.string().uuid(),
  companyName: z.string(),
  roleTitle: z.string(),
  startDate: z.string(),
  endDate: z.string().nullable(),
  isCurrent: z.boolean(),
  industry: z.string().nullable(),
  responsibilities: z.string().nullable(),
  achievements: z.string().nullable(),
  technologies: z.array(z.string()),
  sortOrder: z.number().int(),
  createdByUserId: z.string().uuid(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export const specialistReferenceListQuerySchema = z.object({
  includeInactive: z.coerce.boolean().default(false),
});

export const specialistSkillListQuerySchema = specialistReferenceListQuerySchema.extend({
  categoryId: z.string().uuid().optional(),
});

export const specialistSpecializationCreateRequestSchema = z
  .object({
    slug: slugSchema,
    name: z.string().trim().min(1).max(160),
    description: specialistReferenceDescriptionSchema,
    status: specialistReferenceStatusSchema.default("active"),
    sortOrder: referenceSortOrderSchema.default(0),
  })
  .strict();

export const specialistSpecializationUpdateRequestSchema = z
  .object({
    slug: slugSchema.optional(),
    name: z.string().trim().min(1).max(160).optional(),
    description: specialistReferenceDescriptionSchema,
    status: specialistReferenceStatusSchema.optional(),
    sortOrder: referenceSortOrderSchema.optional(),
  })
  .strict();

export const specialistGradeCreateRequestSchema = z
  .object({
    slug: slugSchema,
    name: z.string().trim().min(1).max(120),
    description: specialistReferenceDescriptionSchema,
    level: z.number().int().min(1).max(20),
    status: specialistReferenceStatusSchema.default("active"),
    sortOrder: referenceSortOrderSchema.default(0),
  })
  .strict();

export const specialistGradeUpdateRequestSchema = z
  .object({
    slug: slugSchema.optional(),
    name: z.string().trim().min(1).max(120).optional(),
    description: specialistReferenceDescriptionSchema,
    level: z.number().int().min(1).max(20).optional(),
    status: specialistReferenceStatusSchema.optional(),
    sortOrder: referenceSortOrderSchema.optional(),
  })
  .strict();

export const specialistSkillCategoryCreateRequestSchema = z
  .object({
    slug: slugSchema,
    name: z.string().trim().min(1).max(160),
    description: specialistReferenceDescriptionSchema,
    status: specialistReferenceStatusSchema.default("active"),
    sortOrder: referenceSortOrderSchema.default(0),
  })
  .strict();

export const specialistSkillCategoryUpdateRequestSchema = z
  .object({
    slug: slugSchema.optional(),
    name: z.string().trim().min(1).max(160).optional(),
    description: specialistReferenceDescriptionSchema,
    status: specialistReferenceStatusSchema.optional(),
    sortOrder: referenceSortOrderSchema.optional(),
  })
  .strict();

export const specialistSkillCreateRequestSchema = z
  .object({
    categoryId: z.string().uuid().nullable().optional(),
    slug: slugSchema,
    name: z.string().trim().min(1).max(160),
    description: specialistReferenceDescriptionSchema,
    status: specialistReferenceStatusSchema.default("active"),
    sortOrder: referenceSortOrderSchema.default(0),
  })
  .strict();

export const specialistSkillUpdateRequestSchema = z
  .object({
    categoryId: z.string().uuid().nullable().optional(),
    slug: slugSchema.optional(),
    name: z.string().trim().min(1).max(160).optional(),
    description: specialistReferenceDescriptionSchema,
    status: specialistReferenceStatusSchema.optional(),
    sortOrder: referenceSortOrderSchema.optional(),
  })
  .strict();

export const specialistSkillSynonymCreateRequestSchema = z
  .object({
    term: z.string().trim().min(1).max(160),
  })
  .strict();

export const specialistProfileSkillCreateRequestSchema = z
  .object({
    skillId: z.string().uuid(),
    level: skillLevelSchema,
    yearsOfExperience: skillYearsSchema.optional(),
    lastUsedAt: skillLastUsedAtSchema.optional(),
    isKeySkill: z.boolean().default(false),
    source: specialistProfileSkillSourceSchema.default("self_reported"),
  })
  .strict();

export const specialistProfileSkillUpdateRequestSchema = z
  .object({
    level: skillLevelSchema.optional(),
    yearsOfExperience: skillYearsSchema.optional(),
    lastUsedAt: skillLastUsedAtSchema.optional(),
    isKeySkill: z.boolean().optional(),
    verificationState: specialistProfileSkillVerificationStateSchema.optional(),
    source: specialistProfileSkillSourceSchema.optional(),
  })
  .strict();

export const specialistWorkExperienceCreateRequestSchema = z
  .object({
    companyName: z.string().trim().min(1).max(160),
    roleTitle: z.string().trim().min(1).max(160),
    startDate: isoDateSchema,
    endDate: nullableIsoDateSchema.optional(),
    isCurrent: z.boolean().default(false),
    industry: nullableString(160).optional(),
    responsibilities: nullableString(4_000).optional(),
    achievements: nullableString(4_000).optional(),
    technologies: technologiesSchema.default([]),
    sortOrder: sortOrderSchema.default(0),
  })
  .strict();

export const specialistWorkExperienceUpdateRequestSchema = z
  .object({
    companyName: z.string().trim().min(1).max(160).optional(),
    roleTitle: z.string().trim().min(1).max(160).optional(),
    startDate: isoDateSchema.optional(),
    endDate: nullableIsoDateSchema.optional(),
    isCurrent: z.boolean().optional(),
    industry: nullableString(160).optional(),
    responsibilities: nullableString(4_000).optional(),
    achievements: nullableString(4_000).optional(),
    technologies: technologiesSchema.optional(),
    sortOrder: sortOrderSchema.optional(),
  })
  .strict();

export const specialistProfileSchema = z.object({
  id: z.string().uuid(),
  ownershipType: specialistOwnershipTypeSchema,
  status: specialistProfileStatusSchema,
  ownerUserId: z.string().uuid().nullable(),
  vendorCompanyId: z.string().uuid().nullable(),
  createdByUserId: z.string().uuid(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  countryCode: z.string().nullable(),
  city: z.string().nullable(),
  timezone: z.string().nullable(),
  primarySpecialization: z.string().nullable(),
  grade: z.string().nullable(),
  primarySpecializationId: z.string().uuid().nullable(),
  gradeId: z.string().uuid().nullable(),
  yearsOfExperience: z.number().int().nullable(),
  summary: z.string().nullable(),
  archivedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export const specialistCreateRequestSchema = z.discriminatedUnion("ownershipType", [
  z.object({ ownershipType: z.literal("independent") }).strict(),
  z.object({ ownershipType: z.literal("vendor_owned"), vendorCompanyId: z.string().uuid() }).strict(),
]);

export const specialistProfileUpdateRequestSchema = z
  .object({
    firstName: nullableString(100).optional(),
    lastName: nullableString(100).optional(),
    countryCode: nullableCountryCode.optional(),
    city: nullableString(120).optional(),
    timezone: nullableString(64).optional(),
    primarySpecialization: nullableString(160).optional(),
    grade: nullableString(80).optional(),
    primarySpecializationId: z.string().uuid().nullable().optional(),
    gradeId: z.string().uuid().nullable().optional(),
    yearsOfExperience: nullableYearsOfExperience.optional(),
    summary: nullableString(2_000).optional(),
  })
  .strict();

export const specialistListQuerySchema = z.object({
  vendorCompanyId: z.string().uuid().optional(),
  ownershipType: specialistOwnershipTypeSchema.optional(),
  includeArchived: z.coerce.boolean().default(false),
});

export const specialistListResponseSchema = z.object({
  items: z.array(specialistProfileSchema),
  total: z.number().int().nonnegative(),
});

export const specialistSpecializationListResponseSchema = z.object({
  items: z.array(specialistSpecializationSchema),
  total: z.number().int().nonnegative(),
});

export const specialistGradeListResponseSchema = z.object({
  items: z.array(specialistGradeSchema),
  total: z.number().int().nonnegative(),
});

export const specialistSkillCategoryListResponseSchema = z.object({
  items: z.array(specialistSkillCategorySchema),
  total: z.number().int().nonnegative(),
});

export const specialistSkillListResponseSchema = z.object({
  items: z.array(specialistSkillSchema),
  total: z.number().int().nonnegative(),
});

export const specialistSkillSynonymListResponseSchema = z.object({
  items: z.array(specialistSkillSynonymSchema),
  total: z.number().int().nonnegative(),
});

export const specialistProfileSkillListResponseSchema = z.object({
  items: z.array(specialistProfileSkillSchema),
  total: z.number().int().nonnegative(),
});

export const specialistWorkExperienceListResponseSchema = z.object({
  items: z.array(specialistWorkExperienceSchema),
  total: z.number().int().nonnegative(),
});

export type SpecialistOwnershipType = z.infer<typeof specialistOwnershipTypeSchema>;
export type SpecialistProfileStatus = z.infer<typeof specialistProfileStatusSchema>;
export type SpecialistReferenceStatus = z.infer<typeof specialistReferenceStatusSchema>;
export type SpecialistProfileSkillVerificationState = z.infer<
  typeof specialistProfileSkillVerificationStateSchema
>;
export type SpecialistProfileSkillSource = z.infer<typeof specialistProfileSkillSourceSchema>;
export type SpecialistSpecialization = z.infer<typeof specialistSpecializationSchema>;
export type SpecialistGrade = z.infer<typeof specialistGradeSchema>;
export type SpecialistSkillCategory = z.infer<typeof specialistSkillCategorySchema>;
export type SpecialistSkill = z.infer<typeof specialistSkillSchema>;
export type SpecialistSkillSynonym = z.infer<typeof specialistSkillSynonymSchema>;
export type SpecialistProfileSkill = z.infer<typeof specialistProfileSkillSchema>;
export type SpecialistWorkExperience = z.infer<typeof specialistWorkExperienceSchema>;
export type SpecialistProfile = z.infer<typeof specialistProfileSchema>;
export type SpecialistCreateRequest = z.infer<typeof specialistCreateRequestSchema>;
export type SpecialistProfileUpdateRequest = z.infer<
  typeof specialistProfileUpdateRequestSchema
>;
export type SpecialistListQuery = z.infer<typeof specialistListQuerySchema>;
export type SpecialistListResponse = z.infer<typeof specialistListResponseSchema>;
export type SpecialistReferenceListQuery = z.infer<
  typeof specialistReferenceListQuerySchema
>;
export type SpecialistSkillListQuery = z.infer<typeof specialistSkillListQuerySchema>;
export type SpecialistSpecializationCreateRequest = z.infer<
  typeof specialistSpecializationCreateRequestSchema
>;
export type SpecialistSpecializationUpdateRequest = z.infer<
  typeof specialistSpecializationUpdateRequestSchema
>;
export type SpecialistGradeCreateRequest = z.infer<typeof specialistGradeCreateRequestSchema>;
export type SpecialistGradeUpdateRequest = z.infer<typeof specialistGradeUpdateRequestSchema>;
export type SpecialistSkillCategoryCreateRequest = z.infer<
  typeof specialistSkillCategoryCreateRequestSchema
>;
export type SpecialistSkillCategoryUpdateRequest = z.infer<
  typeof specialistSkillCategoryUpdateRequestSchema
>;
export type SpecialistSkillCreateRequest = z.infer<typeof specialistSkillCreateRequestSchema>;
export type SpecialistSkillUpdateRequest = z.infer<typeof specialistSkillUpdateRequestSchema>;
export type SpecialistSkillSynonymCreateRequest = z.infer<
  typeof specialistSkillSynonymCreateRequestSchema
>;
export type SpecialistProfileSkillCreateRequest = z.infer<
  typeof specialistProfileSkillCreateRequestSchema
>;
export type SpecialistProfileSkillUpdateRequest = z.infer<
  typeof specialistProfileSkillUpdateRequestSchema
>;
export type SpecialistWorkExperienceCreateRequest = z.infer<
  typeof specialistWorkExperienceCreateRequestSchema
>;
export type SpecialistWorkExperienceUpdateRequest = z.infer<
  typeof specialistWorkExperienceUpdateRequestSchema
>;
export type SpecialistSpecializationListResponse = z.infer<
  typeof specialistSpecializationListResponseSchema
>;
export type SpecialistGradeListResponse = z.infer<typeof specialistGradeListResponseSchema>;
export type SpecialistSkillCategoryListResponse = z.infer<
  typeof specialistSkillCategoryListResponseSchema
>;
export type SpecialistSkillListResponse = z.infer<typeof specialistSkillListResponseSchema>;
export type SpecialistSkillSynonymListResponse = z.infer<
  typeof specialistSkillSynonymListResponseSchema
>;
export type SpecialistProfileSkillListResponse = z.infer<
  typeof specialistProfileSkillListResponseSchema
>;
export type SpecialistWorkExperienceListResponse = z.infer<
  typeof specialistWorkExperienceListResponseSchema
>;
